import { useCallback, useEffect, useRef, useState } from "react";

import {
  TARGET_SAMPLE_RATE,
  downsample,
  mergeChunks,
  peakAmplitude,
  wavBlob,
} from "./wav.js";

/**
 * useDictation — capture, encode, upload. Domain-agnostic: it knows about
 * microphones and a state machine, and nothing about listings. The caller
 * supplies `upload`, which receives the WAV blob and returns whatever its own
 * adapter parsed out of the response.
 *
 * Phases: idle → requesting-permission → recording → encoding → uploading →
 * review, with `error` reachable from any of them.
 *
 * "Didn't hear anything" is an `error` phase with `errorKind: "silence"` rather
 * than a phase of its own — the spec fixes the phase list, and silence needs
 * the same two exits (try again / search manually) every other failure needs.
 */

export const MAX_MS = 60000;
export const WARN_AT_MS = 45000;
/** Peak below this and we never call the API — an accidental tap is free. */
export const SILENCE_FLOOR = 0.02;

/** Can this browser record at all? Cheap and synchronous — no device probe. */
export const isDictationSupported = () => {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext === false) return false; // getUserMedia needs HTTPS
  if (!navigator?.mediaDevices?.getUserMedia) return false;
  return !!(window.AudioContext || window.webkitAudioContext);
};

const ERROR_MESSAGES = {
  permission:
    "Microphone access is blocked. Allow it in your browser's site settings, then try again.",
  device: "No microphone found. Plug one in or use the manual form.",
  busy: "Your microphone is in use by another app. Close it and try again.",
  silence: "We didn't hear anything — nothing was sent. Hold the phone closer and try again.",
  network: "Couldn't process that. Your recording is kept, so you can retry without re-recording.",
  unknown: "Something went wrong with the recording.",
};

const kindFromMediaError = (error) => {
  switch (error?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "permission";
    case "NotFoundError":
    case "OverconstrainedError":
      return "device";
    case "NotReadableError":
    case "AbortError":
      return "busy";
    default:
      return "unknown";
  }
};

export default function useDictation({
  upload,
  maxMs = MAX_MS,
  warnAtMs = WARN_AT_MS,
  silenceFloor = SILENCE_FLOOR,
  // Encoded WAV rate. 16kHz by default; a caller recording longer notes can
  // halve the upload with 8kHz (phone-call quality, still fine for speech).
  sampleRate = TARGET_SAMPLE_RATE,
} = {}) {
  const [phase, setPhase] = useState("idle");
  const [errorKind, setErrorKind] = useState(null);
  // Set when the API told us something more useful than "it failed".
  const [errorOverride, setErrorOverride] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [result, setResult] = useState(null);

  // Everything the capture owns. One ref so teardown is a single call site and
  // a double-tap can't start a second graph behind the first.
  const capture = useRef(null);
  const blobRef = useRef(null);
  const abortRef = useRef(null);
  const mounted = useRef(true);
  const phaseRef = useRef("idle");
  const retryableRef = useRef(true);

  const set = useCallback((next) => {
    phaseRef.current = next;
    if (mounted.current) setPhase(next);
  }, []);

  const teardown = useCallback(() => {
    const c = capture.current;
    capture.current = null;
    if (!c) return null;
    clearInterval(c.timer);
    if (c.processor) {
      c.processor.onaudioprocess = null;
      c.processor.disconnect();
    }
    c.source?.disconnect();
    c.stream?.getTracks().forEach((t) => t.stop());
    // close() rejects on an already-closed context in Safari; nothing to do.
    c.ctx?.close?.().catch(() => {});
    return c;
  }, []);

  const fail = useCallback(
    (kind, message = null, retryable = true) => {
      setErrorKind(kind);
      setErrorOverride(message);
      retryableRef.current = retryable;
      set("error");
    },
    [set]
  );

  const send = useCallback(
    async (blob) => {
      const controller = new AbortController();
      abortRef.current = controller;
      set("uploading");
      try {
        const data = await upload(blob, { signal: controller.signal });
        if (!mounted.current || controller.signal.aborted) return;
        setResult(data);
        set("review");
      } catch (error) {
        if (!mounted.current || controller.signal.aborted) return;
        // The adapter has already turned a known API failure into something
        // sayable; anything else keeps the generic wording.
        fail("network", error?.userFacing ? error.message : null, error?.retryable !== false);
      }
    },
    [upload, set, fail]
  );

  const stop = useCallback(async () => {
    if (phaseRef.current !== "recording") return;
    const c = teardown();
    if (!c) return;

    set("encoding");
    setLevel(0);

    const merged = mergeChunks(c.chunks);
    const samples = downsample(merged, c.sampleRate, sampleRate);

    // Silence gate — measured locally, before a paid call is made.
    if (peakAmplitude(samples) < silenceFloor) {
      blobRef.current = null;
      fail("silence");
      return;
    }

    const blob = wavBlob(samples, sampleRate);
    blobRef.current = blob;
    await send(blob);
  }, [teardown, set, fail, send, silenceFloor, sampleRate]);

  // `stop` is rebuilt on every render; the auto-stop timer must call the latest.
  const stopRef = useRef(stop);
  stopRef.current = stop;

  const start = useCallback(async () => {
    // Debounce the double-tap: one recording at a time, always.
    if (phaseRef.current !== "idle" && phaseRef.current !== "error") return;
    if (!isDictationSupported()) {
      fail("device");
      return;
    }

    setResult(null);
    setErrorKind(null);
    setErrorOverride(null);
    retryableRef.current = true;
    setElapsedMs(0);
    setLevel(0);
    blobRef.current = null;
    set("requesting-permission");

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      fail(kindFromMediaError(error));
      return;
    }

    if (!mounted.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    // ScriptProcessor over AudioWorklet on purpose: a worklet needs a separate
    // module file served at its own URL, and this node is four lines and works
    // on every browser RC supports, iOS Safari included.
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    const chunks = [];
    const startedAt = Date.now();

    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      chunks.push(new Float32Array(input));
      if (mounted.current) setLevel(peakAmplitude(input));
    };

    source.connect(processor);
    // Chrome only pulls a ScriptProcessor that is connected to the graph's
    // destination. Gain would be audible feedback, so route it nowhere useful —
    // the node emits silence because we never write to its output buffer.
    processor.connect(ctx.destination);

    const timer = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      if (mounted.current) setElapsedMs(elapsed);
      if (elapsed >= maxMs) stopRef.current();
    }, 100);

    capture.current = { stream, ctx, source, processor, chunks, timer, sampleRate: ctx.sampleRate };
    set("recording");
  }, [set, fail, maxMs]);

  /** Dismissed, unmounted, or "never mind" — drop everything, persist nothing. */
  const cancel = useCallback(() => {
    teardown();
    abortRef.current?.abort();
    abortRef.current = null;
    blobRef.current = null;
    setResult(null);
    setErrorKind(null);
    setErrorOverride(null);
    retryableRef.current = true;
    setElapsedMs(0);
    setLevel(0);
    set("idle");
  }, [teardown, set]);

  /** Network failure only: the blob is still in memory, so don't re-record. */
  const retry = useCallback(() => {
    if (blobRef.current) return send(blobRef.current);
    return cancel();
  }, [send, cancel]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      teardown();
      abortRef.current?.abort();
    };
  }, [teardown]);

  return {
    phase,
    errorKind,
    errorMessage: errorOverride ?? (errorKind ? ERROR_MESSAGES[errorKind] ?? ERROR_MESSAGES.unknown : null),
    elapsedMs,
    remainingMs: Math.max(0, maxMs - elapsedMs),
    /** True from 45s — the overlay turns the timer into a warning. */
    nearingLimit: phase === "recording" && elapsedMs >= warnAtMs,
    level,
    result,
    canRetry: !!blobRef.current && retryableRef.current,
    start,
    stop,
    cancel,
    retry,
  };
}
